import React, { useState } from "react";
import { Upload, FileText, X, CheckCircle2, AlertTriangle, FileJson } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

// Required fields per row
const REQUIRED = ["title", "brand", "model", "year", "vehicle_type", "transmission", "fuel_type", "city", "location_text", "price_per_day"];

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length);
  if (lines.length < 2) throw new Error("CSV needs a header row and at least one data row");
  const headers = splitCsvLine(lines[0]);
  return lines.slice(1).map((line) => {
    const cells = splitCsvLine(line);
    const row = {};
    headers.forEach((h, i) => {
      row[h.trim()] = (cells[i] ?? "").trim();
    });
    return row;
  });
}

function splitCsvLine(line) {
  // simple CSV parser supporting double-quoted fields with commas
  const result = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; continue; }
    if (c === '"') { inQ = !inQ; continue; }
    if (c === "," && !inQ) { result.push(cur); cur = ""; continue; }
    cur += c;
  }
  result.push(cur);
  return result;
}

export default function BulkUploadModal({ onClose, onDone }) {
  const [mode, setMode] = useState("csv");
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    const txt = await file.text();
    setText(txt);
    parseInput(txt);
  };

  const parseInput = (raw = text) => {
    try {
      let rows;
      if (mode === "csv") rows = parseCSV(raw);
      else rows = JSON.parse(raw);
      if (!Array.isArray(rows)) throw new Error("Expected an array of vehicles");
      // validate
      const missing = rows.map((r, i) => {
        const m = REQUIRED.filter((k) => !(k in r) || r[k] === "" || r[k] == null);
        return m.length ? { row: i + 1, missing: m } : null;
      }).filter(Boolean);
      setParsed({ rows, missing });
    } catch (e) {
      toast.error(`Parse error: ${e.message}`);
      setParsed(null);
    }
  };

  const submit = async () => {
    if (!parsed?.rows?.length) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/owner/vehicles/bulk", { vehicles: parsed.rows });
      setResult(data);
      if (data.created_count > 0) toast.success(`${data.created_count} vehicles published`);
      if (data.errors?.length) toast.error(`${data.errors.length} rows failed`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const sampleCsv = "title,brand,model,year,vehicle_type,transmission,fuel_type,seats,city,location_text,price_per_day,price_per_week,description,photos,features,delivery_available,delivery_fee,driver_available,driver_fee_per_day,km_per_day,excess_km_rate\n\"Hyundai i20 – City runabout\",Hyundai,i20,2023,compact,manual,petrol,5,Cape Town,Observatory,499,2999,Reliable and fuel-efficient,https://images.unsplash.com/photo-1606152421802-db97b9c7a11b?w=800,Bluetooth|Aircon|USB,true,150,false,0,200,3.5\n\"Isuzu D-Max – Bush ready\",Isuzu,D-Max LSE,2023,bakkie,automatic,diesel,5,Pretoria,Centurion,1299,7800,Tough double cab,https://images.unsplash.com/photo-1612544409025-c1ec2a9f8cda?w=800,4x4|Tow bar,true,250,true,800,300,4";

  const sampleJson = JSON.stringify([
    {
      title: "Hyundai i20 – City runabout",
      brand: "Hyundai", model: "i20", year: 2023,
      vehicle_type: "compact", transmission: "manual", fuel_type: "petrol", seats: 5,
      city: "Cape Town", location_text: "Observatory",
      price_per_day: 499, price_per_week: 2999,
      description: "Reliable and fuel-efficient",
      photos: ["https://images.unsplash.com/photo-1606152421802-db97b9c7a11b?w=800"],
      features: ["Bluetooth", "Aircon", "USB"],
      delivery_available: true, delivery_fee: 150,
      driver_available: false, driver_fee_per_day: 0,
      km_per_day: 200, excess_km_rate: 3.5,
    },
  ], null, 2);

  if (result) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0a1f4d]/40 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl p-8">
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-[#10B981]/15 text-[#10B981] mx-auto flex items-center justify-center"><CheckCircle2 size={28}/></div>
            <h2 className="font-brand text-2xl text-[#0a1f4d] mt-4">Bulk import complete</h2>
            <p className="text-sm text-[#334155] mt-2">{result.created_count} vehicles published successfully{result.errors?.length ? `, ${result.errors.length} failed.` : "."}</p>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-5 max-h-48 overflow-y-auto bg-[#FEE2E2]/40 rounded-xl p-3">
              {result.errors.map((e, i) => (
                <p key={i} className="text-xs text-[#B91C1C]"><span className="font-bold">Row {e.row}:</span> {e.error}</p>
              ))}
            </div>
          )}
          <button onClick={onDone} className="btn-primary w-full mt-6 text-sm" data-testid="bulk-done">Back to dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#0a1f4d]/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">Fleet</p>
            <h2 className="font-brand text-2xl text-[#0a1f4d]">Bulk import vehicles</h2>
          </div>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#0a1f4d]" aria-label="Close" data-testid="bulk-close"><X size={18}/></button>
        </div>

        <div className="px-6 pt-5">
          <div className="flex gap-2">
            <button onClick={() => { setMode("csv"); setParsed(null); }} className={`px-4 py-2 rounded-full text-sm font-semibold border ${mode === "csv" ? "bg-[#0a1f4d] text-white border-[#0a1f4d]" : "bg-white text-[#0a1f4d] border-[#E2E8F0]"}`} data-testid="bulk-mode-csv">
              <FileText size={14} className="inline mr-1" /> CSV
            </button>
            <button onClick={() => { setMode("json"); setParsed(null); }} className={`px-4 py-2 rounded-full text-sm font-semibold border ${mode === "json" ? "bg-[#0a1f4d] text-white border-[#0a1f4d]" : "bg-white text-[#0a1f4d] border-[#E2E8F0]"}`} data-testid="bulk-mode-json">
              <FileJson size={14} className="inline mr-1" /> JSON
            </button>
            <button onClick={() => { setText(mode === "csv" ? sampleCsv : sampleJson); parseInput(mode === "csv" ? sampleCsv : sampleJson); }} className="ml-auto text-xs font-semibold text-[#1d7ffd] hover:underline" data-testid="bulk-load-sample">
              Load sample
            </button>
          </div>
        </div>

        <div className="p-6">
          <label className="block border-2 border-dashed border-[#E2E8F0] rounded-2xl p-5 text-center cursor-pointer hover:border-[#1d7ffd] hover:bg-[#1d7ffd]/5 transition-colors">
            <input type="file" accept={mode === "csv" ? ".csv,text/csv" : ".json,application/json"} className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="bulk-file" />
            <Upload size={18} className="mx-auto text-[#64748B]" />
            <p className="text-sm font-semibold text-[#0a1f4d] mt-2">Upload a {mode.toUpperCase()} file</p>
            <p className="text-xs text-[#64748B] mt-1">or paste below</p>
          </label>

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={mode === "csv" ? "title,brand,model,year,..." : "[ { \"title\": \"...\", \"brand\": \"...\", ... } ]"}
            className="mt-3 w-full bg-[#F7F8FA] border border-[#E2E8F0] rounded-xl px-4 py-3 text-xs font-mono text-[#0a1f4d] focus:border-[#1d7ffd] outline-none min-h-[180px]"
            data-testid="bulk-text"
          />

          <div className="mt-4 flex items-center justify-between">
            <button onClick={() => parseInput()} className="btn-outline !py-2 text-sm" data-testid="bulk-parse">Validate</button>
            {parsed && (
              <p className="text-xs font-semibold text-[#0a1f4d]">
                {parsed.rows.length} row{parsed.rows.length !== 1 ? "s" : ""} detected
                {parsed.missing.length > 0 && <span className="text-[#B91C1C]"> · {parsed.missing.length} with missing fields</span>}
              </p>
            )}
          </div>

          {parsed?.missing?.length > 0 && (
            <div className="mt-3 bg-[#FEF3C7] rounded-xl p-3">
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#92400E]">
                <AlertTriangle size={12}/> These rows will be skipped:
              </div>
              <div className="mt-2 max-h-32 overflow-y-auto">
                {parsed.missing.map((m) => (
                  <p key={m.row} className="text-xs text-[#92400E]"><span className="font-bold">Row {m.row}:</span> missing {m.missing.join(", ")}</p>
                ))}
              </div>
            </div>
          )}

          <p className="text-[11px] text-[#64748B] mt-3">
            Required fields: <span className="font-semibold">{REQUIRED.join(", ")}</span>.
            Lists (photos, features) use <code>|</code> separator in CSV.
          </p>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E2E8F0] px-6 py-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="btn-outline !py-2.5 text-sm">Cancel</button>
          <button onClick={submit} disabled={!parsed?.rows?.length || submitting} className="btn-primary text-sm disabled:opacity-60" data-testid="bulk-submit">
            {submitting ? "Importing…" : `Import ${parsed?.rows?.length || 0} vehicles`}
          </button>
        </div>
      </div>
    </div>
  );
}
